import { logger } from '../util/logger';
import { getMediaDevices, getPreferredAudioOutputId, invariant, getPreferredAudioOutputLabel, setPreferredAudioOutput, getPreferredAudioInputLabel, setPreferredAudioInput, } from '@streem/toolbox';
const AUDIO_EVENTS_TO_CAPTURE = [
    'canplay',
    'canplaythrough',
    'emptied',
    'playing',
    'pause',
    'ended',
    'volumechange',
    'suspend',
    'waiting',
    'stalled',
    'abort',
    'error',
    'loadeddata',
];
// exported for testing only!  Use singleton below.
export class AppAudio {
    constructor() {
        this.audioOutputDeviceChangedCallbacks = new Set();
        this.audioInputDeviceChangedCallbacks = new Set();
        this.shouldPlayRingtone = false;
        this.playReservationAudio = async () => {
            invariant(this.reservationAudio, 'Reservation audio is not initialized');
            if (this.reservationAudio) {
                await this.setPreferredAudioOutput(this.reservationAudio);
                logger.info(`Playing reservation audio`);
                await this.reservationAudio.play();
            }
        };
        this.pauseReservationAudio = async () => {
            logger.info(`Pausing reservation audio`);
            this.reservationAudio?.pause();
        };
        this.ringtoneEndedHandler = () => {
            // ST-9203: This class used to set loop=true on the HTMLAudioElement to rely on
            //    the browser to automatically restart the ringtone.  However, we found cases
            //    where that did not happen, so we are manually restarting it to try and avoid
            //    that issue.
            if (this.shouldPlayRingtone === true) {
                this.startRingtoneAudio();
            }
            else {
                logger.debug('not restarting ringtone');
            }
        };
    }
    initialize(handleRingtonePlaying) {
        this.initRingtone();
        this.handleRingtonePlaying = handleRingtonePlaying;
    }
    subscribeToAudioOutputDeviceChanged(callback) {
        this.audioOutputDeviceChangedCallbacks.add(callback);
    }
    unsubscribeToAudioOutputDeviceChanged(callback) {
        this.audioOutputDeviceChangedCallbacks.delete(callback);
    }
    subscribeToAudioInputDeviceChanged(callback) {
        this.audioInputDeviceChangedCallbacks.add(callback);
    }
    unsubscribeToAudioInputDeviceChanged(callback) {
        this.audioInputDeviceChangedCallbacks.delete(callback);
    }
    startRingtoneAudio() {
        invariant(this.ringtoneAudio, 'Ringtone audio is not initialized');
        this.shouldPlayRingtone = true;
        this.ringtoneAudio.play().catch(error => {
            logger.error(`Failed to play ringtone: ${error}`);
            this.shouldPlayRingtone = false;
        });
        this.ringtoneAudioPaused = this.ringtoneAudio.paused;
    }
    async playRingtoneAudio() {
        invariant(this.ringtoneAudio, 'Ringtone audio is not initialized');
        await this.setPreferredAudioOutput(this.ringtoneAudio);
        logger.info(`Playing ringtone`);
        this.startRingtoneAudio();
    }
    async pauseRingtoneAudio() {
        this.shouldPlayRingtone = false;
        this.ringtoneAudio?.pause();
        this.ringtoneAudioPaused = this.ringtoneAudio?.paused ?? true;
    }
    removeReservationAudioListener() {
        this.reservationAudio?.removeEventListener('ended', this.endedListenerHandler);
    }
    async setPreferredAudioOutput(audioElement) {
        if (typeof audioElement.setSinkId !== 'function') {
            logger.info(`Browser does not support setSinkId.`);
            return;
        }
        const preferredOutputId = getPreferredAudioOutputId();
        let sinkId = preferredOutputId || 'default';
        const devices = await getMediaDevices('audiooutput');
        if (!devices.some((device) => device.deviceId === preferredOutputId)) {
            logger.warn(`Could not find preferred preferredOutputId=${preferredOutputId} Resetting preferred device to default`);
            logger.info(`Available devices=${JSON.stringify(devices)}`);
            const defaultDevice = devices.find((device) => device.deviceId === 'default');
            setPreferredAudioOutput('default', defaultDevice?.label || '');
            sinkId = 'default';
        }
        await audioElement.setSinkId(sinkId);
        document.body.appendChild(audioElement);
        logger.info(`Audio output set to preferred output ${getPreferredAudioOutputId()}`);
    }
    endedListenerHandler() {
        logger.info('Reservation ringtone ended');
    }
    initRingtone() {
        if (typeof window == 'undefined') {
            return;
        }
        // Safari & Chrome are very strict about playing audio.
        // They will only play audio if it's user initiated, so we listen for first click here.
        this.once(window.document.body, 'click').then(() => {
            this.createAudio();
            if (this.reservationAudio) {
                this.reservationAudio.addEventListener('ended', this.endedListenerHandler);
            }
            // These event listeners don't need to removed explicitly since the ringtoneAudio is a singleton
            // It's created upon login and destroyed when the user logs out and navigates away from the app.
            logger.info(`Adding event listeners to ringtone`);
            AUDIO_EVENTS_TO_CAPTURE.forEach(event => {
                this.ringtoneAudio?.addEventListener(event, () => {
                    logger.info(`Ringtone audio - received ${event} event`);
                    if (event === 'playing') {
                        this.handleRingtonePlaying();
                    }
                    if (event === 'ended') {
                        this.ringtoneEndedHandler();
                    }
                });
            });
            logger.info(`Listening on media device changes`);
            navigator.mediaDevices.addEventListener('devicechange', async () => {
                const devices = await getMediaDevices();
                const audioDevices = devices.filter((device) => device?.kind === 'audioinput' || device?.kind === 'audiooutput');
                logger.info(`Received ondevicechange event. Available media devices are ${JSON.stringify(audioDevices)}`);
                const preferredOutputId = getPreferredAudioOutputId();
                const preferredOutputDevice = audioDevices.find((device) => device.deviceId === preferredOutputId && device.kind === 'audiooutput');
                logger.info(`Preferred output device: ${preferredOutputId} exists: ${JSON.stringify(preferredOutputDevice)}`);
                const preferredAudioInputLabel = getPreferredAudioInputLabel();
                const preferredInputDeviceByLabel = audioDevices.find(device => device.label === preferredAudioInputLabel);
                if (!preferredInputDeviceByLabel) {
                    const fallbackInputDeviceId = getPreferredAudioOutputId();
                    const fallbackInputDevice = audioDevices.find(device => device.deviceId === fallbackInputDeviceId);
                    if (fallbackInputDevice) {
                        setPreferredAudioInput(fallbackInputDevice.deviceId, fallbackInputDevice.label);
                        this.audioInputDeviceChangedCallbacks.forEach(callback => callback(fallbackInputDevice));
                    }
                }
                if (!preferredOutputDevice) {
                    const defaultDevice = audioDevices.find((device) => device.deviceId === 'default');
                    if (defaultDevice) {
                        logger.warn(`Could not find preferred preferredOutputId=${preferredOutputId} Resetting preferred
                            device to default: ${JSON.stringify(defaultDevice)}`);
                        setPreferredAudioOutput('default', defaultDevice.label);
                        this.audioOutputDeviceChangedCallbacks.forEach(callback => callback(defaultDevice));
                    }
                    else {
                        logger.error('Unexpected state: Default Audio Output device not found!');
                    }
                }
                else if (preferredOutputId == 'default') {
                    const defaultDevice = audioDevices.find((device) => device.deviceId === 'default');
                    const preferredAudioOutputLabel = getPreferredAudioOutputLabel();
                    if (defaultDevice &&
                        preferredAudioOutputLabel &&
                        preferredAudioOutputLabel !== defaultDevice.label) {
                        logger.info(`Preferred default device has changed from ${preferredAudioOutputLabel} to ${defaultDevice.label}`);
                        setPreferredAudioOutput(defaultDevice.deviceId, defaultDevice.label);
                        this.audioOutputDeviceChangedCallbacks.forEach(callback => callback(defaultDevice));
                    }
                }
            });
        });
    }
    // Once the ringtone audio has played, this will prevent the user from accidentally re-playing the audio with their keyboard media keys
    disabledMediaKeys() {
        const mediaActions = [
            'play',
            'pause',
            'stop',
            'seekbackward',
            'seekforward',
            'seekto',
            'previoustrack',
            'nexttrack',
        ];
        mediaActions.forEach(action => {
            try {
                navigator.mediaSession.setActionHandler &&
                    navigator.mediaSession.setActionHandler(action, () => { });
            }
            catch (e) {
                // Ignoring error, which can happen for the `stop` event on old Android devices
            }
        });
    }
    createAudio() {
        // Safari lacks a 'mediaSession' API, so the 'play/pause' button will still control playback of the loaded audio ringtone
        if ('mediaSession' in navigator) {
            this.disabledMediaKeys();
        }
        if (!this.ringtoneAudio) {
            this.ringtoneAudio = new Audio(`${window.location.origin}/streem_ringtone.mp3`);
        }
        if (!this.reservationAudio) {
            this.reservationAudio = new Audio(`${window.location.origin}/reservation_sound.mp3`);
        }
        logger.info('User has interacted with the page, audio is now enabled');
    }
    /**
     * Will run a function once and only once upon a given event on an element
     * @param el The element that will host the event
     * @param event The event to monitor
     */
    once(el, event) {
        return new Promise(resolve => {
            el.addEventListener(event, function onEvent() {
                el.removeEventListener(event, onEvent);
                resolve();
            });
        });
    }
}
export const audio = new AppAudio();
//# sourceMappingURL=sounds.js.map